import * as React from 'react';
// Import hooks.
import {
	useInputBuildingAddressSummary,
	UseInputBuildingAddressSummaryResults,
} from '@app/hooks/view/input';
// Import 
import {
	FormPopupConfirmationProps,
	SubformInputSettingsBuildingInfoAddressProps,
} from '@app/props';
// Import api models.
import {
	AddressLocationWithAccuracy,
} from '@app/models/api';
// Import props.
import {
	SubformBuildingInfoAddressProps,
} from '@app/props';

interface UseSubformBuildingInfoAddressParameters {
	id: string;
	allowEdits: boolean;
	confirmationChangeRequestMessage: string;
	requireConfirmationOnChangeRequest: boolean;
	inputSettingsProps?: SubformInputSettingsBuildingInfoAddressProps;
	propertyAddress?: AddressLocationWithAccuracy;
	onRequestToChangeBuildingAddress: () => void;
	onRequestToChangeBuildingLocation: () => void;
}

interface UseSubformBuildingInfoAddressResults {
	areAllInputsDisabled: boolean;
	buildingAddressSummaryInput: UseInputBuildingAddressSummaryResults;
	applySubformBuildingInfoAddressProps: () => SubformBuildingInfoAddressProps;
}

const useSubformBuildingInfoAddress = (params: UseSubformBuildingInfoAddressParameters): UseSubformBuildingInfoAddressResults => {

	// #region exported methods

	const applySubformBuildingInfoAddressProps = (): SubformBuildingInfoAddressProps => {
		return {
			buildingAddressSummaryInput,
			showBuildingAddressSummaryInput,
			applyRequestToChangeBuildingAddressFormPopupConfirmationProps,
			applyRequestToChangeBuildingLocationFormPopupConfirmationProps,
		};
	}

	/**
	 * Returns props for the request to change building address confirmation popup form. 
	 **/
	const applyRequestToChangeBuildingAddressFormPopupConfirmationProps = (): FormPopupConfirmationProps => {
		return {
			isOpen: openRequestToChangeBuildingAddressConfirmationPopup,
			onClose: cancelConfirmRequestToChangeBuildingAddress,
			onConfirm: confirmRequestToChangeBuildingAddress,
			message: params.confirmationChangeRequestMessage ?? String.empty(),
		};
	};

	/**
	 * Returns props for the request to change building location confirmation popup form. 
	 **/
	const applyRequestToChangeBuildingLocationFormPopupConfirmationProps = (): FormPopupConfirmationProps => {
		return {
			isOpen: openRequestToChangeBuildingLocationConfirmationPopup,
			onClose: cancelConfirmRequestToChangeBuildingLocation,
			onConfirm: confirmRequestToChangeBuildingLocation,
			message: params.confirmationChangeRequestMessage ?? String.empty(),
		};
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Confirms request to change building address if user chooses to. 
	 **/
	const confirmRequestToChangeBuildingAddress = (): void => {
		setOpenRequestToChangeBuildingAddressConfirmationPopup(false);
		params.onRequestToChangeBuildingAddress();
	}

	/**
	 * Confirms request to change building location if user chooses to. 
	 **/
	const confirmRequestToChangeBuildingLocation = (): void => {
		setOpenRequestToChangeBuildingLocationConfirmationPopup(false);
		params.onRequestToChangeBuildingLocation();
	}


	/**
	 * Cancels the confirmation prompt for request to change building address. 
	 **/
	const cancelConfirmRequestToChangeBuildingAddress = (): void => {
		setOpenRequestToChangeBuildingAddressConfirmationPopup(false);
	}

	/**
	 * Cancels the confirmation prompt for request to change building location. 
	 **/
	const cancelConfirmRequestToChangeBuildingLocation = (): void => {
		setOpenRequestToChangeBuildingLocationConfirmationPopup(false);
	}

	/**
	 * Makes request call to change building address. 
	 **/
	const requestToChangeBuildingAddress = (): void => {
		// Make sure the confirmation popup appears if it's required.
		if (params.requireConfirmationOnChangeRequest) {
			setOpenRequestToChangeBuildingAddressConfirmationPopup(true);
		}
		else {
			params.onRequestToChangeBuildingAddress();
		}
	}

	/**
	 * Makes request call to change building location. 
	 **/
	const requestToChangeBuildingLocation = (): void => {
		// Make sure the confirmation popup appears if it's required.
		if (params.requireConfirmationOnChangeRequest) {
			setOpenRequestToChangeBuildingLocationConfirmationPopup(true);
		}
		else {
			params.onRequestToChangeBuildingLocation();
		}
	}

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for building address summary input. 
	 **/
	const generateIdForBuildingAddressSummaryInput = (): string => {
		return `${generateIdForForm()}-address`;
	}

	//#endregion id generators

	// #endregion private methods


	// #region variables

	// Input settings visibility.
	const showBuildingAddressSummaryInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.buildingAddressSummaryInput !== 'hidden',
		[params.inputSettingsProps?.buildingAddressSummaryInput]
	);

	// Input settings disable.
	const isBuildingAddressSummaryInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showBuildingAddressSummaryInput
		|| params.inputSettingsProps?.buildingAddressSummaryInput === 'disabled',
		[
			params.allowEdits,
			showBuildingAddressSummaryInput,
			params.inputSettingsProps?.buildingAddressSummaryInput,
		]
	);

	// Setup the building address summary input.  This should only be updated directly through the props, and is not included in the form save data.  Any validation errors should always appear.
	const buildingAddressSummaryInput: UseInputBuildingAddressSummaryResults = useInputBuildingAddressSummary({
		id: generateIdForBuildingAddressSummaryInput(),
		label: String.empty(),
		disabled: isBuildingAddressSummaryInputDisabled,
		required: true,
		showValidation: true,
		value: params.propertyAddress ?? new AddressLocationWithAccuracy(),
		onChangeAddress: requestToChangeBuildingAddress,
		onChangeLocation: requestToChangeBuildingLocation,
	});


	// State variables.
	const [openRequestToChangeBuildingAddressConfirmationPopup, setOpenRequestToChangeBuildingAddressConfirmationPopup] = React.useState<boolean>(false);
	const [openRequestToChangeBuildingLocationConfirmationPopup, setOpenRequestToChangeBuildingLocationConfirmationPopup] = React.useState<boolean>(false);

	// Check to see if all inputs are disabled.
	const areAllInputsDisabled: boolean = React.useMemo(() => {
		return buildingAddressSummaryInput.isDisabled

	}, [
		buildingAddressSummaryInput.isDisabled,
	]);

	// #endregion variables


	return {
		areAllInputsDisabled,
		buildingAddressSummaryInput,
		applySubformBuildingInfoAddressProps,
	};
}

// Export parameters.
export {
	UseSubformBuildingInfoAddressParameters,
}

// Export results.
export {
	UseSubformBuildingInfoAddressResults,
}

export default useSubformBuildingInfoAddress;